import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { cancelBet, claimBet, effectiveStakes, getBetById, getSchedule, resolveBet, respondToBet, type BetRow } from '../api';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../auth';
import { rigaDate, rigaTime } from '../format';
import { toast } from '../toast';
import { haptic } from '../haptic';

interface LogEvent {
  at: string;
  icon: string;
  title: string;
  detail?: string | null;
  tone?: 'pitch' | 'red' | 'cyan' | 'dim';
}

function buildActivityLog(bet: BetRow): LogEvent[] {
  const log: LogEvent[] = [];
  const isMarketplace = bet.is_marketplace === 1;
  log.push({
    at: bet.created_at,
    icon: isMarketplace ? '📣' : '＋',
    title: isMarketplace
      ? `${bet.proposer} posted to marketplace${bet.expires_at ? ` (expires ${bet.expires_at})` : ' (no expiry)'}`
      : `${bet.proposer} proposed the bet`,
    detail: bet.message ? `"${bet.message}"` : null,
    tone: 'cyan',
  });
  if (bet.responded_at) {
    if (bet.status === 'declined') {
      log.push({ at: bet.responded_at, icon: '✗', title: `${bet.opponent} declined`, tone: 'red' });
    } else if (bet.status === 'cancelled') {
      log.push({ at: bet.responded_at, icon: '↺', title: `${bet.proposer} cancelled`, tone: 'dim' });
    } else if (isMarketplace) {
      log.push({ at: bet.responded_at, icon: '🤝', title: `${bet.opponent} claimed the marketplace bet`, tone: 'pitch' });
    } else {
      log.push({ at: bet.responded_at, icon: '✓', title: `${bet.opponent} accepted`, tone: 'pitch' });
    }
  } else if (bet.status === 'cancelled') {
    // Cancelled without a stored timestamp; fall back to created_at as a marker.
    log.push({ at: bet.created_at, icon: '↺', title: `${bet.proposer} cancelled`, tone: 'dim' });
  }
  if (bet.resolution_proposer_pick) {
    log.push({
      at: bet.settled_at ?? bet.responded_at ?? bet.created_at,
      icon: '🪙',
      title: `${bet.proposer} claims: ${bet.resolution_proposer_pick === 'proposer' ? bet.proposer : bet.resolution_proposer_pick === 'opponent' ? bet.opponent : 'Void'}`,
      tone: 'dim',
    });
  }
  if (bet.resolution_opponent_pick) {
    log.push({
      at: bet.settled_at ?? bet.responded_at ?? bet.created_at,
      icon: '🪙',
      title: `${bet.opponent} claims: ${bet.resolution_opponent_pick === 'proposer' ? bet.proposer : bet.resolution_opponent_pick === 'opponent' ? bet.opponent : 'Void'}`,
      tone: 'dim',
    });
  }
  if (bet.settled_at && bet.status === 'settled') {
    const winnerName = bet.winner === 'proposer' ? bet.proposer : bet.winner === 'opponent' ? bet.opponent : null;
    const stakes = effectiveStakes(bet);
    const won = bet.winner === 'proposer' ? stakes.opponent : bet.winner === 'opponent' ? stakes.proposer : 0;
    log.push({
      at: bet.settled_at,
      icon: '🏆',
      title: winnerName ? `Resolved: ${winnerName} won €${won}` : 'Resolved: voided',
      tone: winnerName ? 'pitch' : 'dim',
    });
  }
  if (bet.status === 'voided') {
    log.push({ at: bet.settled_at ?? bet.created_at, icon: '⊘', title: 'Voided', tone: 'dim' });
  }
  log.sort((a, b) => a.at.localeCompare(b.at));
  return log;
}

export default function BetDetail() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['bet', id],
    queryFn: () => getBetById(id),
    refetchInterval: 30_000,
  });
  const { data: schedule } = useQuery({ queryKey: ['schedule'], queryFn: getSchedule });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['bet', id] });
    qc.invalidateQueries({ queryKey: ['bets'] });
    qc.invalidateQueries({ queryKey: ['public-bets'] });
  };
  const respond = useMutation({
    mutationFn: (action: 'accept' | 'decline') => respondToBet(id, action),
    onSuccess: (_, action) => { haptic.tap(); toast.success(action === 'accept' ? 'Bet accepted.' : 'Bet declined.'); },
    onError: (e: Error) => toast.error(e.message),
    onSettled: invalidate,
  });
  const cancel = useMutation({
    mutationFn: () => cancelBet(id),
    onSuccess: () => { haptic.tap(); toast.success('Bet cancelled.'); },
    onError: (e: Error) => toast.error(e.message),
    onSettled: invalidate,
  });
  const resolve = useMutation({
    mutationFn: (winner: 'proposer' | 'opponent' | 'void') => resolveBet(id, winner),
    onSuccess: () => { haptic.success(); toast.success('Resolution recorded.'); },
    onError: (e: Error) => toast.error(e.message),
    onSettled: invalidate,
  });
  const claim = useMutation({
    mutationFn: () => claimBet(id),
    onSuccess: () => { haptic.success(); toast.success("Claimed — you're now the opponent."); },
    onError: (e: Error) => toast.error(e.message),
    onSettled: invalidate,
  });

  if (isLoading) return <PageHeader title="Bet" subtitle="Loading…" />;
  if (error || !data) {
    return (
      <>
        <PageHeader title="Bet" subtitle="Not found." />
        <div className="px-4 lg:px-0">
          <Link to="/bets" className="text-xs text-pitch-400 hover:underline">← Back to bets</Link>
        </div>
      </>
    );
  }

  const bet = data.bet;
  const match = bet.match_id ? schedule?.matches.find((m) => m.id === bet.match_id) : null;
  const involved = !!user && (bet.proposer === user || bet.opponent === user);
  const youWin = user && bet.winner && ((bet.proposer === user && bet.winner === 'proposer') || (bet.opponent === user && bet.winner === 'opponent'));
  const youLose = user && bet.winner && ((bet.proposer === user && bet.winner === 'opponent') || (bet.opponent === user && bet.winner === 'proposer'));
  const log = buildActivityLog(bet);
  const stakes = effectiveStakes(bet);
  const isVariable = stakes.proposer !== stakes.opponent;
  const proposerSide = bet.proposer_side === 'yes' ? 'YES — agrees' : bet.proposer_side === 'no' ? 'NO — disagrees' : null;
  const opponentSide = bet.proposer_side === 'yes' ? 'NO — disagrees' : bet.proposer_side === 'no' ? 'YES — agrees' : null;
  const myClaim = user === bet.proposer ? bet.resolution_proposer_pick : user === bet.opponent ? bet.resolution_opponent_pick : null;
  const otherClaim = user === bet.proposer ? bet.resolution_opponent_pick : user === bet.opponent ? bet.resolution_proposer_pick : null;
  const otherName = user === bet.proposer ? bet.opponent : user === bet.opponent ? bet.proposer : null;

  return (
    <>
      <PageHeader
        title={`${bet.proposer} vs ${bet.opponent}`}
        subtitle={
          <button onClick={() => nav(-1)} className="text-pitch-400 hover:underline">← Back</button>
        }
      />
      <div className="px-4 lg:px-0 space-y-3">
        <article className="card p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[11px] flex-wrap">
                <Link to={`/players/${encodeURIComponent(bet.proposer)}`} className="font-semibold hover:underline">{bet.proposer}</Link>
                <span className="text-dim">vs</span>
                <Link to={`/players/${encodeURIComponent(bet.opponent)}`} className="font-semibold hover:underline">{bet.opponent}</Link>
                <StatusPill status={bet.status} youWin={youWin} youLose={youLose} />
                {isVariable && <span className="pill bg-ink-700 text-dim font-mono">€{stakes.proposer} : €{stakes.opponent}</span>}
              </div>
              <div className="mt-2 text-base leading-snug">{bet.description}</div>
              {match && (
                <Link to={`/matches/${match.id}`} className="block mt-1.5 text-xs text-dim hover:underline">
                  {match.home_flag ?? '🏳️'} {match.home_name} vs {match.away_flag ?? '🏳️'} {match.away_name}
                  {' · '}{rigaDate(match.utc_kickoff)} · {rigaTime(match.utc_kickoff)}
                </Link>
              )}
              {bet.message && <div className="mt-2 text-xs text-dim italic">"{bet.message}"</div>}
            </div>
            <div className="text-right shrink-0">
              <div className="stat text-2xl font-semibold">€{Math.max(stakes.proposer, stakes.opponent)}</div>
              <div className="text-[10px] uppercase tracking-wider text-dim">pot</div>
            </div>
          </div>

          {(proposerSide || isVariable) && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-pitch-500/10 ring-1 ring-pitch-500/20 p-2.5">
                <div className="text-[10px] uppercase tracking-wider text-pitch-400">{bet.proposer}'s side</div>
                <div className="text-sm font-semibold mt-0.5">{proposerSide ?? '—'}</div>
                <div className="text-[10px] text-dim mt-1">Risks €{stakes.proposer} · wins €{stakes.opponent}</div>
              </div>
              <div className="rounded-lg bg-cyan-500/10 ring-1 ring-cyan-500/20 p-2.5">
                <div className="text-[10px] uppercase tracking-wider text-cyan-400">{bet.opponent}'s side</div>
                <div className="text-sm font-semibold mt-0.5">{opponentSide ?? '—'}</div>
                <div className="text-[10px] text-dim mt-1">Risks €{stakes.opponent} · wins €{stakes.proposer}</div>
              </div>
            </div>
          )}

          {(() => {
            const isOpenMarket = bet.status === 'open' && bet.is_marketplace === 1;
            const canClaim = isOpenMarket && !!user && user !== bet.proposer;
            const showActions = canClaim
              || (isOpenMarket && bet.proposer === user)
              || (involved && (bet.status === 'pending' || bet.status === 'active'));
            if (!showActions) return null;
            return (
              <div className="mt-3 flex flex-wrap gap-2">
                {canClaim && (
                  <ActionBtn label={`Claim · risk €${stakes.opponent}`} onClick={() => claim.mutate()} primary disabled={claim.isPending} />
                )}
                {isOpenMarket && bet.proposer === user && (
                  <ActionBtn label="Withdraw" onClick={() => cancel.mutate()} disabled={cancel.isPending} />
                )}
                {bet.status === 'pending' && bet.opponent === user && (
                  <>
                    <ActionBtn label="Accept" onClick={() => respond.mutate('accept')} primary disabled={respond.isPending} />
                    <ActionBtn label="Decline" onClick={() => respond.mutate('decline')} disabled={respond.isPending} />
                  </>
                )}
                {bet.status === 'pending' && bet.proposer === user && (
                  <ActionBtn label="Cancel" onClick={() => cancel.mutate()} disabled={cancel.isPending} />
                )}
                {bet.status === 'active' && involved && (
                  <DetailSettlement
                    bet={bet} me={user!} otherName={otherName!}
                    myClaim={myClaim} otherClaim={otherClaim}
                    resolve={(w) => resolve.mutate(w)} pending={resolve.isPending}
                  />
                )}
              </div>
            );
          })()}
        </article>

        <section className="card p-4">
          <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Activity log</h2>
          <ol className="space-y-2.5">
            {log.map((e, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm ${
                  e.tone === 'pitch' ? 'bg-pitch-500/20 text-pitch-400'
                  : e.tone === 'red' ? 'bg-red-500/20 text-red-400'
                  : e.tone === 'cyan' ? 'bg-cyan-500/20 text-cyan-400'
                  : 'bg-ink-700 text-dim'
                }`}>{e.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{e.title}</div>
                  {e.detail && <div className="text-xs text-dim italic mt-0.5">{e.detail}</div>}
                  <div className="text-[10px] text-dim font-mono">{rigaDate(e.at)} · {rigaTime(e.at)}</div>
                </div>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </>
  );
}

function DetailSettlement({ bet, me, otherName, myClaim, otherClaim, resolve, pending }: {
  bet: BetRow; me: string; otherName: string;
  myClaim: BetRow['winner']; otherClaim: BetRow['winner'];
  resolve: (w: 'proposer' | 'opponent' | 'void') => void; pending: boolean;
}) {
  const iAmProposer = bet.proposer === me;
  const meAsClaim: 'proposer' | 'opponent' = iAmProposer ? 'proposer' : 'opponent';
  const otherAsClaim: 'proposer' | 'opponent' = iAmProposer ? 'opponent' : 'proposer';

  if (myClaim && !otherClaim) {
    return <div className="text-xs text-dim italic w-full">Waiting for {otherName} to confirm the outcome.</div>;
  }
  if (otherClaim && !myClaim) {
    const label = otherClaim === 'void'
      ? `${otherName} proposes voiding the bet.`
      : otherClaim === meAsClaim
        ? `${otherName} says you won.`
        : `${otherName} says they won.`;
    return (
      <div className="flex flex-col gap-2 w-full">
        <div className="text-xs text-chalk">{label}</div>
        <div className="flex flex-wrap gap-2">
          <ActionBtn label="Approve" onClick={() => resolve(otherClaim)} primary disabled={pending} />
          <ActionBtn label="Dispute (I won)" onClick={() => resolve(meAsClaim)} disabled={pending} />
          {otherClaim !== 'void' && <ActionBtn label="Void" onClick={() => resolve('void')} disabled={pending} />}
        </div>
      </div>
    );
  }
  if (myClaim && otherClaim && myClaim !== otherClaim) {
    return (
      <div className="flex flex-col gap-2 w-full">
        <div className="text-xs text-red-400">Outcome contested. Both sides need to agree — change your pick or void.</div>
        <div className="flex flex-wrap gap-2">
          <ActionBtn label={`Change to "${otherName} won"`} onClick={() => resolve(otherAsClaim)} disabled={pending} />
          <ActionBtn label="Both agree it's void" onClick={() => resolve('void')} disabled={pending} />
        </div>
      </div>
    );
  }
  return (
    <>
      <ActionBtn label="I won" onClick={() => resolve(meAsClaim)} primary disabled={pending} />
      <ActionBtn label={`${otherName} won`} onClick={() => resolve(otherAsClaim)} disabled={pending} />
      <ActionBtn label="Void" onClick={() => resolve('void')} disabled={pending} />
    </>
  );
}

function StatusPill({ status, youWin, youLose }: { status: string; youWin?: unknown; youLose?: unknown }) {
  if (status === 'settled') {
    if (youWin) return <span className="pill bg-pitch-500/20 text-pitch-400">You won</span>;
    if (youLose) return <span className="pill bg-red-500/20 text-red-400">You lost</span>;
    return <span className="pill bg-ink-700 text-chalk">Settled</span>;
  }
  const labels: Record<string, string> = {
    pending: 'Pending', open: 'Open · marketplace', active: 'Active', declined: 'Declined', cancelled: 'Cancelled', voided: 'Voided',
  };
  return <span className={`pill ${status === 'open' ? 'bg-cyan-500/15 text-cyan-400' : 'bg-ink-700 text-dim'}`}>{labels[status] ?? status}</span>;
}

function ActionBtn({ label, onClick, primary, disabled }: { label: string; onClick: () => void; primary?: boolean; disabled?: boolean }) {
  return (
    <button
      onClick={onClick} disabled={disabled}
      className={`px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50 ${
        primary ? 'bg-pitch-500 text-ink-950 hover:bg-pitch-400' : 'bg-ink-800 text-chalk hover:bg-ink-700'
      }`}
    >{label}</button>
  );
}
